import { useTranslation } from 'react-i18next';

import StatusDescriptionModal from 'components/stateless/Modal/common/status/StatusDescriptionModal';
import Pagination from 'components/stateless/Pagination/Pagination';
import { Tr } from 'components/stateless/Table';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { AWS_EMAIL_EVENT_TYPE, EMAIL_SENT_STATUS } from 'enums';
import type { PageableType } from 'hooks/usePageable';
import type { FactoringNoticeReceiverVOModel } from 'models/vo/FactoringNoticeReceiverVO';
import getStatusTextClass from 'utils/classNames/getStatusTextClass';
import { ModalSize, ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';

interface AnchorDeptSellingNoticeDetailRecipientListProps {
  data?: FactoringNoticeReceiverVOModel[];
  paginate(pageNumber: number, rowCount: number): Promise<void>;
  pageable: PageableType;
}

function AnchorDeptSellingNoticeDetailRecipientList({
  data,
  paginate,
  pageable,
}: AnchorDeptSellingNoticeDetailRecipientListProps) {
  const { t } = useTranslation();
  const modal = useModal();

  const RECIPIENT_LIST_TABLE_HEADER: HeaderType[] = [
    {
      headerText: t('text:User_Name'),
    },
    {
      headerText: t('text:Authority'),
    },
    {
      headerText: t('text:User_Email_Address'),
    },
    {
      headerText: t('text:Receiving_Email_Address'),
    },
    {
      headerText: t('text:Reception_Status'),
      hasStatusDescription: true,
      showStatusDescriptionFunc: () => {
        modal.show(
          <StatusDescriptionModal
            statusDescriptionEnum={Object.values(EMAIL_SENT_STATUS)}
            statusDescriptionModalType="TEXT"
            statusDescriptionEnumType="EMAIL_SENT_STATUS"
          />,
          {
            modalType: ModalType.ALERT,
            modalSize: ModalSize.XL,
            closeBtnText: t('text:Close'),
          },
        );
      },
    },
    {
      headerText: t('text:Sent_Time'),
    },
    {
      headerText: t('text:Reason_for_Failure'),
      hasStatusDescription: true,
      showStatusDescriptionFunc: () => {
        modal.show(
          <StatusDescriptionModal
            statusDescriptionEnum={[
              AWS_EMAIL_EVENT_TYPE.BOUNCE,
              AWS_EMAIL_EVENT_TYPE.NETWORK_ERROR,
              AWS_EMAIL_EVENT_TYPE.MAILBOX_FULL,
              AWS_EMAIL_EVENT_TYPE.MESSAGE_TOO_LARGE,
            ]}
            statusDescriptionModalType="TEXT"
            statusDescriptionEnumType="AWS_EMAIL_EVENT_TYPE"
          />,
          {
            modalType: ModalType.ALERT,
            modalSize: ModalSize.XL,
            closeBtnText: t('text:Close'),
          },
        );
      },
    },
  ];

  return (
    <div className="content-area">
      <SectionTitle title={t('text:Recipient_List')} />
      <TableBorder>
        <TableHeader header={RECIPIENT_LIST_TABLE_HEADER} />
        <TableBody numOfCol={RECIPIENT_LIST_TABLE_HEADER.length}>
          {data?.map((item, i) => {
            return (
              <Tr key={i}>
                <Td data={item.userName} />
                <Td data={t(`code:authority-type.${item.authorityType}`)} format="code" />
                <Td data={item.currentUserEmailAddress} />
                <Td data={item.recentSentEmailAddress} />
                <Td
                  className={getStatusTextClass('EMAIL_SENT_STATUS', item.emailSentStatus)}
                  data={t(`code:email-sent-status.${item.emailSentStatus}`)}
                  format="code"
                />
                <Td data={item.sentDateTime} format="datetime" />
                <Td
                  data={
                    item.emailSentStatus === EMAIL_SENT_STATUS.FAIL
                      ? t(`code:aws-email-event-type.${item.awsEmailEventType}`)
                      : '-'
                  }
                  format="code"
                />
              </Tr>
            );
          })}
        </TableBody>
      </TableBorder>
      <Pagination pageable={pageable} paginate={paginate} />
    </div>
  );
}

export default AnchorDeptSellingNoticeDetailRecipientList;
